import { useEffect, useMemo, useState } from 'react';
import { Cable, ExternalLink, LoaderCircle, PlugZap, Search, ShieldAlert, Unplug } from 'lucide-react';
import { engineFetchJson } from '@/lib/engineHttp';

interface SourceCatalogEntry {
  slug: string;
  name: string;
  description: string;
  icon: string | null;
  category: string;
  auth_type: string;
  auth_scopes: string[];
  setup_guide: string;
  data_types: string[];
  requires_api_key: boolean;
  is_bundled: boolean;
  status: string;
}

interface EnabledSourceEntry extends SourceCatalogEntry {
  connection_status: string;
  external_username: string | null;
  last_sync_at: string | null;
  last_error: string | null;
  enabled: boolean;
}

interface SourceEnableResponse extends EnabledSourceEntry {
  authorize_url: string | null;
}

interface SourceDisableResponse {
  slug: string;
  disabled: boolean;
}

interface McpToolSummary {
  name: string;
  description: string | null;
  input_schema: Record<string, unknown> | null;
}

interface McpEnableResponse {
  slug: string;
  status: 'connected' | 'oauth_required' | 'tools_unavailable';
  mcp_server_url: string;
  mcp_auth_method: string;
  connection_status: string;
  selected_tool: string | null;
  tools: McpToolSummary[];
  tools_error: string | null;
  resource_metadata_url: string | null;
  message: string | null;
}

interface McpOAuthInitiateResponse {
  slug: string;
  status: string;
  authorization_url: string;
  state: string;
  redirect_uri: string;
  resource: string;
  scopes: string[];
}

interface IngestionResult {
  connection_id: string;
  source_type: string;
  ingested_count: number;
  skipped_count: number;
  error_count: number;
  last_sync_at: string;
}

type McpAuthMethod = 'bearer' | 'api_key' | 'oauth' | 'none';

function statusTone(status: string): { label: string; color: string } {
  switch (status) {
    case 'connected':
      return { label: 'Connected', color: 'var(--sp-ok)' };
    case 'oauth_required':
      return { label: 'OAuth required', color: 'var(--sp-warn)' };
    case 'tools_unavailable':
      return { label: 'Connected, tools pending', color: 'var(--sp-warn)' };
    case 'error':
      return { label: 'Error', color: 'var(--sp-error)' };
    case 'coming_soon':
      return { label: 'Coming soon', color: 'var(--sp-text-dim)' };
    case 'not_connected':
      return { label: 'Available', color: 'var(--sp-text-muted)' };
    default:
      return { label: status.replace(/_/g, ' '), color: 'var(--sp-text-muted)' };
  }
}

function formatSync(value: string | null): string {
  if (!value) return 'Never synced';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never synced';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function categoryTitle(value: string): string {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function SourcesPage() {
  const [catalog, setCatalog] = useState<SourceCatalogEntry[]>([]);
  const [enabled, setEnabled] = useState<EnabledSourceEntry[]>([]);
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionSlug, setActionSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [mcpServerUrl, setMcpServerUrl] = useState('');
  const [mcpAuthMethod, setMcpAuthMethod] = useState<McpAuthMethod>('bearer');
  const [mcpAuthValue, setMcpAuthValue] = useState('');
  const [mcpLastResponse, setMcpLastResponse] = useState<McpEnableResponse | null>(null);
  // Cookie-auth (NotebookLM) + file_path (Obsidian) + ingest. The cookie is a
  // secret (B2): held only in this field, sent in the POST body, never logged.
  const [cookieValue, setCookieValue] = useState('');
  const [vaultPath, setVaultPath] = useState('');
  const [ingestQuery, setIngestQuery] = useState('');
  const [ingestResult, setIngestResult] = useState<IngestionResult | null>(null);

  const refresh = async () => {
    const [catalogData, enabledData] = await Promise.all([
      engineFetchJson<SourceCatalogEntry[]>('/sources/catalog'),
      engineFetchJson<EnabledSourceEntry[]>('/sources/enabled'),
    ]);
    setCatalog(catalogData);
    setEnabled(enabledData);
    if (!selectedSlug && catalogData.length > 0) {
      setSelectedSlug(catalogData[0].slug);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [catalogData, enabledData] = await Promise.all([
          engineFetchJson<SourceCatalogEntry[]>('/sources/catalog'),
          engineFetchJson<EnabledSourceEntry[]>('/sources/enabled').catch(() => []),
        ]);
        if (cancelled) return;
        setCatalog(catalogData);
        setEnabled(enabledData);
        if (catalogData.length > 0) setSelectedSlug((current) => current || catalogData[0].slug);
      } catch (loadError) {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : 'Failed to load source catalog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const enabledBySlug = useMemo(
    () => new Map(enabled.map((entry) => [entry.slug, entry])),
    [enabled],
  );

  const selectedEntry = useMemo(
    () => catalog.find((entry) => entry.slug === selectedSlug) ?? null,
    [catalog, selectedSlug],
  );

  const categories = useMemo(() => ['all', ...Array.from(new Set(catalog.map((entry) => entry.category))).sort()], [catalog]);

  const filteredEntries = useMemo(() => {
    const lowered = query.trim().toLowerCase();
    return catalog.filter((entry) => {
      const matchesCategory = selectedCategory === 'all' || entry.category === selectedCategory;
      const haystack = `${entry.name} ${entry.description} ${entry.category} ${entry.auth_type}`.toLowerCase();
      const matchesQuery = !lowered || haystack.includes(lowered);
      return matchesCategory && matchesQuery;
    });
  }, [catalog, query, selectedCategory]);

  const handleEnable = async (entry: SourceCatalogEntry) => {
    setActionSlug(entry.slug);
    setNotice(null);
    setMcpLastResponse(null);
    try {
      const result = await engineFetchJson<SourceEnableResponse>(
        `/sources/enable/${entry.slug}?return_to=${encodeURIComponent('/sources')}`,
        { method: 'POST' },
      );
      if (result.authorize_url) {
        window.open(result.authorize_url, '_blank', 'width=640,height=800');
        setNotice(`${entry.name} authorization opened in a new window.`);
      } else {
        setNotice(`${entry.name} enabled.`);
      }
      await refresh();
    } catch (actionError) {
      setNotice(actionError instanceof Error ? actionError.message : 'Enable failed');
    } finally {
      setActionSlug(null);
    }
  };

  const handleDisable = async (slug: string) => {
    setActionSlug(slug);
    setNotice(null);
    try {
      await engineFetchJson<SourceDisableResponse>(`/sources/disable/${slug}`, { method: 'DELETE' });
      setNotice(`${slug} disabled.`);
      setMcpLastResponse(null);
      await refresh();
    } catch (actionError) {
      setNotice(actionError instanceof Error ? actionError.message : 'Disable failed');
    } finally {
      setActionSlug(null);
    }
  };

  const handleMcpConnect = async (entry: SourceCatalogEntry) => {
    setActionSlug(entry.slug);
    setNotice(null);
    setMcpLastResponse(null);
    try {
      const response = await engineFetchJson<McpEnableResponse>(`/sources/enable/${entry.slug}/mcp`, {
        method: 'POST',
        body: JSON.stringify({
          mcp_server_url: mcpServerUrl || undefined,
          mcp_auth_method: mcpAuthMethod,
          auth_value: mcpAuthMethod === 'none' || mcpAuthMethod === 'oauth' ? undefined : mcpAuthValue || undefined,
        }),
      });
      setMcpLastResponse(response);
      if (response.status === 'oauth_required') {
        setNotice(response.message || 'OAuth 2.1 is required for this MCP server.');
      } else if (response.tools_error) {
        setNotice(response.tools_error);
      } else {
        setNotice(`${entry.name} connected.`);
      }
      await refresh();
    } catch (actionError) {
      setNotice(actionError instanceof Error ? actionError.message : 'MCP connection failed');
    } finally {
      setActionSlug(null);
    }
  };

  const handleMcpOAuth = async (entry: SourceCatalogEntry) => {
    setActionSlug(entry.slug);
    setNotice(null);
    try {
      const response = await engineFetchJson<McpOAuthInitiateResponse>(`/sources/enable/${entry.slug}/mcp/oauth`, {
        method: 'POST',
        body: JSON.stringify({
          mcp_server_url: mcpServerUrl || undefined,
          return_to: '/sources',
        }),
      });
      window.open(response.authorization_url, '_blank', 'width=640,height=820');
      setNotice(`OAuth flow opened for ${entry.name}.`);
    } catch (actionError) {
      setNotice(actionError instanceof Error ? actionError.message : 'MCP OAuth initiation failed');
    } finally {
      setActionSlug(null);
    }
  };

  // Cookie-auth connect (NotebookLM). The cookie is a secret (B2): it travels in
  // the POST body only — never a query string, URL, or log — and the field is
  // cleared on success so it does not linger in component state / the DOM.
  const handleCookieConnect = async (entry: SourceCatalogEntry) => {
    if (!cookieValue.trim()) {
      setNotice('Paste your NotebookLM session cookie first.');
      return;
    }
    setActionSlug(entry.slug);
    setNotice(null);
    setIngestResult(null);
    try {
      await engineFetchJson<SourceEnableResponse>(`/sources/enable/${entry.slug}/cookie`, {
        method: 'POST',
        body: JSON.stringify({ cookie: cookieValue }),
      });
      setCookieValue('');
      setNotice(`${entry.name} connected.`);
      await refresh();
    } catch (actionError) {
      setNotice(actionError instanceof Error ? actionError.message : 'Cookie binding failed');
    } finally {
      setActionSlug(null);
    }
  };

  // file_path connect (Obsidian). Enables the connector; the vault path is sent
  // in the body for forward-compatibility with the path-binding backend.
  const handleFilePathConnect = async (entry: SourceCatalogEntry) => {
    if (!vaultPath.trim()) {
      setNotice('Enter your vault path first.');
      return;
    }
    setActionSlug(entry.slug);
    setNotice(null);
    try {
      await engineFetchJson<SourceEnableResponse>(
        `/sources/enable/${entry.slug}?return_to=${encodeURIComponent('/sources')}`,
        { method: 'POST', body: JSON.stringify({ vault_path: vaultPath }) },
      );
      setNotice(`${entry.name} enabled for ${vaultPath}.`);
      await refresh();
    } catch (actionError) {
      setNotice(actionError instanceof Error ? actionError.message : 'Enable failed');
    } finally {
      setActionSlug(null);
    }
  };

  // Run one ingest cycle for a connected cookie source (NotebookLM): the query
  // drives the bridge → crystallizes grounded material into the Codex.
  const handleIngest = async (entry: SourceCatalogEntry) => {
    if (!ingestQuery.trim()) {
      setNotice('Enter a query to import from this source.');
      return;
    }
    setActionSlug(entry.slug);
    setNotice(null);
    setIngestResult(null);
    try {
      const result = await engineFetchJson<IngestionResult>(`/sources/${entry.slug}/ingest`, {
        method: 'POST',
        body: JSON.stringify({ tool_arguments: { query: ingestQuery } }),
      });
      setIngestResult(result);
      setNotice(`Imported ${result.ingested_count} item(s) from ${entry.name} into the Codex.`);
      await refresh();
    } catch (actionError) {
      setNotice(actionError instanceof Error ? actionError.message : 'Ingest failed');
    } finally {
      setActionSlug(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-space-4)' }}>
      <section
        style={{
          border: '1px solid var(--sp-border)',
          borderRadius: 'var(--sp-radius-lg)',
          background: 'var(--sp-bg-elev)',
          padding: 'var(--sp-space-4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
              Source Catalog
            </div>
            <h1 style={{ margin: '6px 0 0', fontSize: 24 }}>Connect your sources</h1>
            <p style={{ margin: '8px 0 0', maxWidth: 760, color: 'var(--sp-text-muted)' }}>
              Manifest-backed source directory for OAuth connectors, local ingest paths, and MCP servers.
            </p>
          </div>
          <div
            style={{
              minWidth: 180,
              padding: '12px 14px',
              borderRadius: 'var(--sp-radius-lg)',
              background: 'var(--sp-bg-elev-2)',
              border: '1px solid var(--sp-border)',
            }}
          >
            <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
              Enabled Sources
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{enabled.length}</div>
            <div style={{ fontSize: 12, color: 'var(--sp-text-muted)' }}>active for this tenant</div>
          </div>
        </div>
      </section>

      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 360px',
          gap: 'var(--sp-space-4)',
          minHeight: 620,
        }}
      >
        <div
          style={{
            border: '1px solid var(--sp-border)',
            borderRadius: 'var(--sp-radius-lg)',
            background: 'var(--sp-bg-elev)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: 'var(--sp-space-4)',
              borderBottom: '1px solid var(--sp-border)',
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr)',
              gap: 'var(--sp-space-3)',
            }}
          >
            <label style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--sp-text-dim)' }} />
              <input
                className="sp-input"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search sources, auth types, categories..."
                style={{ paddingLeft: 32 }}
              />
            </label>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {categories.map((category) => {
                const active = selectedCategory === category;
                return (
                  <button
                    key={category}
                    type="button"
                    className="sp-btn"
                    onClick={() => setSelectedCategory(category)}
                    style={{
                      background: active ? 'rgba(127, 209, 185, 0.12)' : 'var(--sp-bg-elev-2)',
                      borderColor: active ? 'rgba(127, 209, 185, 0.3)' : 'var(--sp-border)',
                      color: active ? 'var(--sp-accent)' : 'var(--sp-text-muted)',
                    }}
                  >
                    {category === 'all' ? 'All categories' : categoryTitle(category)}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-space-4)' }}>
            {loading ? (
              <div className="sp-empty">
                <LoaderCircle size={18} className="animate-spin" style={{ marginBottom: 8 }} />
                Loading source catalog...
              </div>
            ) : error ? (
              <div className="sp-empty" style={{ color: 'var(--sp-error)' }}>
                {error}
              </div>
            ) : filteredEntries.length === 0 ? (
              <div className="sp-empty">No sources matched this search.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
                {filteredEntries.map((entry) => {
                  const enabledEntry = enabledBySlug.get(entry.slug);
                  const state = statusTone(enabledEntry?.connection_status || entry.status || 'not_connected');
                  const active = entry.slug === selectedSlug;
                  return (
                    <button
                      key={entry.slug}
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        setSelectedSlug(entry.slug);
                        setMcpLastResponse(null);
                      }}
                      style={{
                        textAlign: 'left',
                        borderRadius: 'var(--sp-radius-lg)',
                        border: active
                          ? '1px solid var(--sp-accent)'
                          : '1px solid var(--sp-border)',
                        background: active ? 'rgba(127, 209, 185, 0.14)' : 'var(--sp-bg-elev-2)',
                        boxShadow: active ? '0 0 0 1px var(--sp-accent) inset' : 'none',
                        padding: '14px',
                        color: 'var(--sp-text)',
                        cursor: 'pointer',
                        transition: 'background 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700 }}>{entry.name}</div>
                          <div style={{ marginTop: 4, color: 'var(--sp-text-muted)', fontSize: 12 }}>
                            {categoryTitle(entry.category)} · {entry.auth_type}
                          </div>
                        </div>
                        <span
                          className="mono"
                          style={{
                            fontSize: 10,
                            color: state.color,
                            border: `1px solid ${state.color}`,
                            borderRadius: 999,
                            padding: '2px 8px',
                            textTransform: 'uppercase',
                            height: 'fit-content',
                          }}
                        >
                          {state.label}
                        </span>
                      </div>

                      <div style={{ marginTop: 10, color: 'var(--sp-text-muted)', fontSize: 13, lineHeight: 1.6 }}>
                        {entry.description}
                      </div>

                      <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                        {entry.data_types.map((type) => (
                          <span
                            key={type}
                            className="mono"
                            style={{
                              fontSize: 10,
                              color: 'var(--sp-text-dim)',
                              border: '1px solid var(--sp-border)',
                              borderRadius: 999,
                              padding: '3px 7px',
                            }}
                          >
                            {type}
                          </span>
                        ))}
                      </div>

                      <div style={{ marginTop: 14, fontSize: 12, color: 'var(--sp-text-muted)' }}>
                        {enabledEntry?.external_username
                          ? `Connected as ${enabledEntry.external_username}`
                          : enabledEntry
                            ? formatSync(enabledEntry.last_sync_at)
                            : entry.is_bundled
                              ? 'Bundled with Seitiate'
                              : 'Optional connector'}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <aside
          style={{
            border: '1px solid var(--sp-border)',
            borderRadius: 'var(--sp-radius-lg)',
            background: 'var(--sp-bg-elev)',
            padding: 'var(--sp-space-4)',
            overflowY: 'auto',
          }}
        >
          {notice ? (
            <div
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 'var(--sp-radius)',
                border: '1px solid rgba(127, 209, 185, 0.25)',
                background: 'rgba(127, 209, 185, 0.08)',
                color: 'var(--sp-accent)',
                fontSize: 12,
              }}
            >
              {notice}
            </div>
          ) : null}

          {!selectedEntry ? (
            <div className="sp-empty">Select a source to inspect or connect it.</div>
          ) : (
            <>
              <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                Detail
              </div>
              <h2 style={{ margin: '8px 0 0', fontSize: 22 }}>{selectedEntry.name}</h2>
              <div style={{ marginTop: 6, color: 'var(--sp-text-muted)', fontSize: 13 }}>
                {categoryTitle(selectedEntry.category)} · {selectedEntry.auth_type}
              </div>

              <p style={{ marginTop: 16, color: 'var(--sp-text-muted)', lineHeight: 1.7 }}>
                {selectedEntry.description}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 14 }}>
                <Metric label="Status" value={statusTone(enabledBySlug.get(selectedEntry.slug)?.connection_status || selectedEntry.status).label} />
                <Metric label="Bundled" value={selectedEntry.is_bundled ? 'Yes' : 'No'} />
                <Metric label="API key" value={selectedEntry.requires_api_key ? 'Required' : 'No'} />
                <Metric label="Auth type" value={selectedEntry.auth_type} />
              </div>

              {enabledBySlug.get(selectedEntry.slug)?.last_error ? (
                <div
                  style={{
                    marginTop: 14,
                    padding: '10px 12px',
                    borderRadius: 'var(--sp-radius)',
                    background: 'rgba(224, 104, 104, 0.08)',
                    border: '1px solid rgba(224, 104, 104, 0.25)',
                    color: 'var(--sp-error)',
                    fontSize: 12,
                  }}
                >
                  {enabledBySlug.get(selectedEntry.slug)?.last_error}
                </div>
              ) : null}

              <div style={{ marginTop: 18 }}>
                <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                  Setup guide
                </div>
                <div
                  style={{
                    marginTop: 10,
                    border: '1px solid var(--sp-border)',
                    borderRadius: 'var(--sp-radius)',
                    background: 'var(--sp-bg-elev-2)',
                    padding: '12px',
                    color: 'var(--sp-text-muted)',
                    fontSize: 13,
                    lineHeight: 1.7,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {selectedEntry.setup_guide}
                </div>
              </div>

              {selectedEntry.auth_type === 'mcp_connection' ? (
                <div style={{ marginTop: 18 }}>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                    MCP configuration
                  </div>
                  <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
                    <input
                      className="sp-input"
                      value={mcpServerUrl}
                      onChange={(event) => setMcpServerUrl(event.target.value)}
                      placeholder={selectedEntry.slug === 'mcp_custom' ? 'https://mcp.example.com' : 'Optional override URL'}
                    />
                    <select
                      className="sp-input"
                      value={mcpAuthMethod}
                      onChange={(event) => setMcpAuthMethod(event.target.value as McpAuthMethod)}
                    >
                      <option value="bearer">Bearer token</option>
                      <option value="api_key">API key</option>
                      <option value="oauth">OAuth 2.1</option>
                      <option value="none">No auth</option>
                    </select>
                    {mcpAuthMethod !== 'none' && mcpAuthMethod !== 'oauth' ? (
                      <input
                        className="sp-input"
                        type="password"
                        value={mcpAuthValue}
                        onChange={(event) => setMcpAuthValue(event.target.value)}
                        placeholder={mcpAuthMethod === 'bearer' ? 'Bearer token' : 'API key'}
                      />
                    ) : null}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="sp-btn sp-btn-primary"
                        onClick={() => void handleMcpConnect(selectedEntry)}
                        disabled={actionSlug === selectedEntry.slug || mcpAuthMethod === 'oauth'}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        {actionSlug === selectedEntry.slug ? <LoaderCircle size={14} className="animate-spin" /> : <Cable size={14} />}
                        Connect MCP
                      </button>
                      <button
                        type="button"
                        className="sp-btn"
                        onClick={() => void handleMcpOAuth(selectedEntry)}
                        disabled={actionSlug === selectedEntry.slug}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <ExternalLink size={14} />
                        Start OAuth
                      </button>
                      {enabledBySlug.get(selectedEntry.slug) ? (
                        <button
                          type="button"
                          className="sp-btn"
                          onClick={() => void handleDisable(selectedEntry.slug)}
                          disabled={actionSlug === selectedEntry.slug}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          <Unplug size={14} />
                          Disconnect
                        </button>
                      ) : null}
                    </div>

                    {mcpLastResponse ? (
                      <div
                        style={{
                          border: '1px solid var(--sp-border)',
                          borderRadius: 'var(--sp-radius)',
                          background: 'var(--sp-bg-elev-2)',
                          padding: '12px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--sp-text-muted)', fontSize: 12 }}>
                          {mcpLastResponse.status === 'oauth_required' ? <ShieldAlert size={14} /> : <PlugZap size={14} />}
                          <span>{statusTone(mcpLastResponse.status).label}</span>
                        </div>
                        {mcpLastResponse.message ? (
                          <div style={{ marginTop: 8, color: 'var(--sp-text-muted)', fontSize: 12 }}>
                            {mcpLastResponse.message}
                          </div>
                        ) : null}
                        {mcpLastResponse.tools.length > 0 ? (
                          <div style={{ marginTop: 10 }}>
                            <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                              Discovered tools
                            </div>
                            <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: 'var(--sp-text-muted)', fontSize: 12 }}>
                              {mcpLastResponse.tools.map((tool) => (
                                <li key={tool.name}>{tool.name}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : selectedEntry.auth_type === 'cookie' ? (
                <div style={{ marginTop: 18 }}>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                    Session cookie
                  </div>
                  <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
                    <input
                      className="sp-input"
                      type="password"
                      value={cookieValue}
                      onChange={(event) => setCookieValue(event.target.value)}
                      placeholder={`Paste your ${selectedEntry.name} session cookie (JSON)`}
                      autoComplete="off"
                      spellCheck={false}
                      aria-label={`${selectedEntry.name} session cookie`}
                    />
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="sp-btn sp-btn-primary"
                        onClick={() => void handleCookieConnect(selectedEntry)}
                        disabled={actionSlug === selectedEntry.slug || !cookieValue.trim()}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        {actionSlug === selectedEntry.slug ? <LoaderCircle size={14} className="animate-spin" /> : <Cable size={14} />}
                        Connect Source
                      </button>
                      {enabledBySlug.get(selectedEntry.slug) ? (
                        <button
                          type="button"
                          className="sp-btn"
                          onClick={() => void handleDisable(selectedEntry.slug)}
                          disabled={actionSlug === selectedEntry.slug}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          <Unplug size={14} />
                          Disconnect
                        </button>
                      ) : null}
                    </div>

                    {enabledBySlug.get(selectedEntry.slug)?.connection_status === 'connected' ? (
                      <div
                        style={{
                          border: '1px solid var(--sp-border)',
                          borderRadius: 'var(--sp-radius)',
                          background: 'var(--sp-bg-elev-2)',
                          padding: '12px',
                          display: 'grid',
                          gap: 10,
                        }}
                      >
                        <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                          Import into Codex
                        </div>
                        <input
                          className="sp-input"
                          value={ingestQuery}
                          onChange={(event) => setIngestQuery(event.target.value)}
                          placeholder="What should we import? (query)"
                          aria-label="Import query"
                        />
                        <button
                          type="button"
                          className="sp-btn sp-btn-primary"
                          onClick={() => void handleIngest(selectedEntry)}
                          disabled={actionSlug === selectedEntry.slug || !ingestQuery.trim()}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifySelf: 'start' }}
                        >
                          {actionSlug === selectedEntry.slug ? <LoaderCircle size={14} className="animate-spin" /> : <PlugZap size={14} />}
                          Run import
                        </button>
                        {ingestResult ? (
                          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>
                            Imported {ingestResult.ingested_count} · skipped {ingestResult.skipped_count} · errors {ingestResult.error_count}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : selectedEntry.auth_type === 'file_path' ? (
                <div style={{ marginTop: 18 }}>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                    Vault path
                  </div>
                  <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
                    <input
                      className="sp-input"
                      value={vaultPath}
                      onChange={(event) => setVaultPath(event.target.value)}
                      placeholder="/path/to/your/Obsidian/vault"
                      aria-label="Vault path"
                    />
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="sp-btn sp-btn-primary"
                        onClick={() => void handleFilePathConnect(selectedEntry)}
                        disabled={actionSlug === selectedEntry.slug || !vaultPath.trim()}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        {actionSlug === selectedEntry.slug ? <LoaderCircle size={14} className="animate-spin" /> : <Cable size={14} />}
                        Connect Source
                      </button>
                      {enabledBySlug.get(selectedEntry.slug) ? (
                        <button
                          type="button"
                          className="sp-btn"
                          onClick={() => void handleDisable(selectedEntry.slug)}
                          disabled={actionSlug === selectedEntry.slug}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          <Unplug size={14} />
                          Disconnect
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {enabledBySlug.get(selectedEntry.slug) ? (
                    <button
                      type="button"
                      className="sp-btn"
                      onClick={() => void handleDisable(selectedEntry.slug)}
                      disabled={actionSlug === selectedEntry.slug}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      {actionSlug === selectedEntry.slug ? <LoaderCircle size={14} className="animate-spin" /> : <Unplug size={14} />}
                      Disconnect
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="sp-btn sp-btn-primary"
                      onClick={() => void handleEnable(selectedEntry)}
                      disabled={actionSlug === selectedEntry.slug}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      {actionSlug === selectedEntry.slug ? <LoaderCircle size={14} className="animate-spin" /> : <Cable size={14} />}
                      Connect Source
                    </button>
                  )}
                </div>
              )}

              {selectedEntry.auth_scopes.length > 0 ? (
                <div style={{ marginTop: 18 }}>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
                    OAuth scopes
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                    {selectedEntry.auth_scopes.map((scope) => (
                      <span
                        key={scope}
                        className="mono"
                        style={{
                          fontSize: 10,
                          color: 'var(--sp-text-muted)',
                          border: '1px solid var(--sp-border)',
                          borderRadius: 999,
                          padding: '4px 8px',
                        }}
                      >
                        {scope}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </aside>
      </section>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        border: '1px solid var(--sp-border)',
        borderRadius: 'var(--sp-radius)',
        background: 'var(--sp-bg-elev-2)',
        padding: '10px 12px',
      }}
    >
      <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600 }}>{value}</div>
    </div>
  );
}
